import type { GraphSeries } from "src/components/charts/engine/types";
import { chartColors } from "src/pages/analytics/constants";
import type { HeatmapData } from "@hightouch/lib/llm/schema/components/schemas";
import { AggregationOption } from "@hightouch/lib/query/visual/types";

/**
 * Transform heatmap data to GraphSeries format for HeatmapTable component
 *
 * The HeatmapTable expects GraphSeries with 2-dimensional grouping data.
 * We transform each row/column combination into a series with proper grouping.
 */
export function transformHeatmapToGraphSeries(
  heatmapData: HeatmapData,
): GraphSeries[] {
  const series: GraphSeries[] = [];

  // Create a GraphSeries for each row/column combination
  heatmapData.rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell, colIndex) => {
      const columnLabel = heatmapData.columns[colIndex];

      // Create a unique key for this combination
      const key = `${row.label}-${columnLabel}`
        .toLowerCase()
        .replace(/\s+/g, "-");

      series.push({
        // Required by GraphSeries type but unused by HeatmapTable
        key,
        description: "",
        legendName: "",
        color: chartColors[rowIndex % chartColors.length]!.color,
        style: "solid" as const,

        // Used by HeatmapTable for series grouping
        metricKey: heatmapData.config.metric,
        metricName: heatmapData.config.metric,

        // Required by GraphSeries type but unused by HeatmapTable
        metricNoun: "row" as const,
        aggregation: AggregationOption.Count,
        metricId: "",
        rawMetricName: "",
        audienceName: "",

        // **CRITICAL**: Grouping creates the heatmap dimensions
        // HeatmapTable uses alias, value, and description from each group
        grouping: [
          {
            column: {
              name: heatmapData.config.rowLabel,
              type: "raw" as const,
              modelId: "", // Empty since this is LLM-generated data
            },
            value: row.label,
            alias: heatmapData.config.rowLabel,
          },
          {
            column: {
              name: heatmapData.config.columnLabel,
              type: "raw" as const,
              modelId: "", // Empty since this is LLM-generated data
            },
            value: columnLabel ?? null,
            alias: heatmapData.config.columnLabel,
          },
        ],

        // Data - HeatmapTable expects a single data point per series
        data: [
          {
            // Used by HeatmapTable to get the cell value and tooltip grouping
            metricValue: cell.value,
            grouping: [
              {
                column: {
                  name: heatmapData.config.rowLabel,
                  type: "raw" as const,
                  modelId: "", // Empty since this is LLM-generated data
                },
                value: row.label,
                alias: heatmapData.config.rowLabel,
              },
              {
                column: {
                  name: heatmapData.config.columnLabel,
                  type: "raw" as const,
                  modelId: "", // Empty since this is LLM-generated data
                },
                value: columnLabel ?? null,
                alias: heatmapData.config.columnLabel,
              },
            ],

            // Required by DataPoint type but unused by HeatmapTable
            calculatedAt: 0,
            seriesKey: key,
            metricName: heatmapData.config.metric,
            aggregation: AggregationOption.Count,
            audienceId: "",
            splitId: null,
          },
        ],
      });
    });
  });

  return series;
}
